import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Box, Snackbar, Alert } from '@mui/material';
import { courseApi } from '../services/api';

interface ScormPlayerProps {
  contentUrl: string;
  lessonId: string;
  lessonSlug: string;
  courseSlug: string;
  onComplete?: (data: ScormData) => void;
  onProgress?: (data: ScormData) => void;
  onNavigateNext?: () => void;
}

interface ScormData {
  lessonId: string;
  completionStatus: string;
  successStatus: string;
  scoreRaw: string;
  scoreMin: string;
  scoreMax: string;
  totalTime: string;
  suspendData: string;
  location: string;
}

// SCORM 1.2 API Implementation
class Scorm12API {
  private data: Record<string, string> = {};
  private initialized = false;
  private finished = false;
  private lastError = '0';
  private onDataChange?: (data: Record<string, string>, version: string) => void;

  constructor(onDataChange?: (data: Record<string, string>, version: string) => void) {
    this.onDataChange = onDataChange;
    this.initializeData();
  }

  private initializeData() {
    this.data = {
      'cmi.core.student_id': '',
      'cmi.core.student_name': '',
      'cmi.core.lesson_location': '',
      'cmi.core.credit': 'credit',
      'cmi.core.lesson_status': 'not attempted',
      'cmi.core.entry': 'ab-initio',
      'cmi.core.score.raw': '',
      'cmi.core.score.max': '100',
      'cmi.core.score.min': '0',
      'cmi.core.total_time': '0000:00:00',
      'cmi.core.lesson_mode': 'normal',
      'cmi.core.exit': '',
      'cmi.core.session_time': '0000:00:00',
      'cmi.suspend_data': '',
      'cmi.launch_data': '',
      'cmi.comments': '',
      'cmi.comments_from_lms': '',
    };
  }

  LMSInitialize(param: string): string {
    if (this.initialized) {
      this.lastError = '101';
      return 'false';
    }
    this.initialized = true;
    this.finished = false;
    this.lastError = '0';
    console.log('[SCORM 1.2] LMSInitialize called');
    return 'true';
  }

  LMSFinish(param: string): string {
    if (!this.initialized) {
      this.lastError = '301';
      return 'false';
    }
    if (this.finished) {
      this.lastError = '101';
      return 'false';
    }
    this.finished = true;
    this.initialized = false;
    this.lastError = '0';
    console.log('[SCORM 1.2] LMSFinish called');
    this.notifyDataChange();
    return 'true';
  }

  LMSGetValue(element: string): string {
    if (!this.initialized) {
      this.lastError = '301';
      return '';
    }

    const value = this.data[element];
    if (value !== undefined) {
      this.lastError = '0';
      console.log(`[SCORM 1.2] LMSGetValue(${element}) = ${value}`);
      return value;
    }

    if (element.endsWith('._count')) {
      this.lastError = '0';
      return '0';
    }

    this.lastError = '401';
    console.log(`[SCORM 1.2] LMSGetValue(${element}) - not found`);
    return '';
  }

  LMSSetValue(element: string, value: string): string {
    if (!this.initialized) {
      this.lastError = '301';
      return 'false';
    }

    const readOnlyElements = [
      'cmi.core.student_id',
      'cmi.core.student_name',
      'cmi.core.credit',
      'cmi.core.entry',
      'cmi.core.total_time',
      'cmi.core.lesson_mode',
      'cmi.launch_data',
      'cmi.comments_from_lms',
    ];

    if (readOnlyElements.includes(element)) {
      this.lastError = '403';
      return 'false';
    }

    this.data[element] = value;
    this.lastError = '0';
    console.log(`[SCORM 1.2] LMSSetValue(${element}, ${value})`);

    if (element.includes('lesson_status') || element.includes('score')) {
      this.notifyDataChange();
    }

    return 'true';
  }

  LMSCommit(param: string): string {
    if (!this.initialized) {
      this.lastError = '301';
      return 'false';
    }
    this.lastError = '0';
    console.log('[SCORM 1.2] LMSCommit called');
    this.notifyDataChange();
    return 'true';
  }

  LMSGetLastError(): string {
    return this.lastError;
  }

  LMSGetErrorString(errorCode: string): string {
    const errorStrings: Record<string, string> = {
      '0': 'No Error',
      '101': 'General Exception',
      '201': 'Invalid argument error',
      '202': 'Element cannot have children',
      '203': 'Element not an array - cannot have count',
      '301': 'Not initialized',
      '401': 'Not implemented error',
      '402': 'Invalid set value, element is a keyword',
      '403': 'Element is read only',
      '404': 'Element is write only',
      '405': 'Incorrect data type',
    };
    return errorStrings[errorCode] || 'Unknown Error';
  }

  LMSGetDiagnostic(errorCode: string): string {
    return this.LMSGetErrorString(errorCode);
  }

  private notifyDataChange() {
    if (this.onDataChange) {
      this.onDataChange({ ...this.data }, '1.2');
    }
  }

  getData(): Record<string, string> {
    return { ...this.data };
  }
}

// SCORM 2004 API Implementation
class Scorm2004API {
  private data: Record<string, string> = {};
  private initialized = false;
  private terminated = false;
  private lastError = '0';
  private onDataChange?: (data: Record<string, string>, version: string) => void;

  constructor(onDataChange?: (data: Record<string, string>, version: string) => void) {
    this.onDataChange = onDataChange;
    this.initializeData();
  }

  private initializeData() {
    this.data = {
      'cmi.learner_id': '',
      'cmi.learner_name': '',
      'cmi.location': '',
      'cmi.credit': 'credit',
      'cmi.completion_status': 'not attempted',
      'cmi.success_status': 'unknown',
      'cmi.entry': 'ab-initio',
      'cmi.score.raw': '',
      'cmi.score.max': '100',
      'cmi.score.min': '0',
      'cmi.score.scaled': '',
      'cmi.total_time': 'PT0H0M0S',
      'cmi.mode': 'normal',
      'cmi.exit': '',
      'cmi.session_time': 'PT0H0M0S',
      'cmi.suspend_data': '',
      'cmi.launch_data': '',
      'cmi.progress_measure': '',
      'cmi.scaled_passing_score': '',
      'cmi.learner_preference.audio_level': '1',
      'cmi.learner_preference.language': '',
      'cmi.learner_preference.delivery_speed': '1',
      'cmi.learner_preference.audio_captioning': '0',
    };
  }

  Initialize(param: string): string {
    if (this.initialized) {
      this.lastError = '103'; // Already initialized
      return 'false';
    }
    if (this.terminated) {
      this.lastError = '104'; // Content instance terminated
      return 'false';
    }
    this.initialized = true;
    this.lastError = '0';
    console.log('[SCORM 2004] Initialize called');
    return 'true';
  }

  Terminate(param: string): string {
    if (!this.initialized) {
      this.lastError = '112'; // Termination before initialization
      return 'false';
    }
    if (this.terminated) {
      this.lastError = '113'; // Termination after termination
      return 'false';
    }
    this.terminated = true;
    this.initialized = false;
    this.lastError = '0';
    console.log('[SCORM 2004] Terminate called');
    this.notifyDataChange();
    return 'true';
  }

  GetValue(element: string): string {
    if (!this.initialized) {
      this.lastError = '122'; // Retrieve data before initialization
      return '';
    }
    if (this.terminated) {
      this.lastError = '123'; // Retrieve data after termination
      return '';
    }

    const value = this.data[element];
    if (value !== undefined) {
      this.lastError = '0';
      console.log(`[SCORM 2004] GetValue(${element}) = ${value}`);
      return value;
    }

    // Handle _count elements
    if (element.endsWith('._count')) {
      this.lastError = '0';
      return '0';
    }

    // Handle _children elements
    if (element.endsWith('._children')) {
      this.lastError = '0';
      return this.getChildren(element);
    }

    this.lastError = '401'; // Undefined data model element
    console.log(`[SCORM 2004] GetValue(${element}) - not found`);
    return '';
  }

  SetValue(element: string, value: string): string {
    if (!this.initialized) {
      this.lastError = '132'; // Store data before initialization
      return 'false';
    }
    if (this.terminated) {
      this.lastError = '133'; // Store data after termination
      return 'false';
    }

    const readOnlyElements = [
      'cmi.learner_id',
      'cmi.learner_name',
      'cmi.credit',
      'cmi.entry',
      'cmi.total_time',
      'cmi.mode',
      'cmi.launch_data',
      'cmi.scaled_passing_score',
      'cmi.completion_threshold',
    ];

    if (readOnlyElements.includes(element)) {
      this.lastError = '404'; // Data model element is read only
      return 'false';
    }

    this.data[element] = value;
    this.lastError = '0';
    console.log(`[SCORM 2004] SetValue(${element}, ${value})`);

    // Notify on important changes
    if (element.includes('completion_status') ||
        element.includes('success_status') ||
        element.includes('score')) {
      this.notifyDataChange();
    }

    return 'true';
  }

  Commit(param: string): string {
    if (!this.initialized) {
      this.lastError = '142'; // Commit before initialization
      return 'false';
    }
    if (this.terminated) {
      this.lastError = '143'; // Commit after termination
      return 'false';
    }
    this.lastError = '0';
    console.log('[SCORM 2004] Commit called');
    this.notifyDataChange();
    return 'true';
  }

  GetLastError(): string {
    return this.lastError;
  }

  GetErrorString(errorCode: string): string {
    const errorStrings: Record<string, string> = {
      '0': 'No Error',
      '101': 'General Exception',
      '102': 'General Initialization Failure',
      '103': 'Already Initialized',
      '104': 'Content Instance Terminated',
      '111': 'General Termination Failure',
      '112': 'Termination Before Initialization',
      '113': 'Termination After Termination',
      '122': 'Retrieve Data Before Initialization',
      '123': 'Retrieve Data After Termination',
      '132': 'Store Data Before Initialization',
      '133': 'Store Data After Termination',
      '142': 'Commit Before Initialization',
      '143': 'Commit After Termination',
      '201': 'General Argument Error',
      '301': 'General Get Failure',
      '351': 'General Set Failure',
      '391': 'General Commit Failure',
      '401': 'Undefined Data Model Element',
      '402': 'Unimplemented Data Model Element',
      '403': 'Data Model Element Value Not Initialized',
      '404': 'Data Model Element Is Read Only',
      '405': 'Data Model Element Is Write Only',
      '406': 'Data Model Element Type Mismatch',
      '407': 'Data Model Element Value Out Of Range',
      '408': 'Data Model Dependency Not Established',
    };
    return errorStrings[errorCode] || 'Unknown Error';
  }

  GetDiagnostic(errorCode: string): string {
    return this.GetErrorString(errorCode);
  }

  private getChildren(element: string): string {
    const childrenMap: Record<string, string> = {
      'cmi.score._children': 'raw,min,max,scaled',
      'cmi.learner_preference._children': 'audio_level,language,delivery_speed,audio_captioning',
      'cmi.objectives._children': 'id,score,success_status,completion_status,description',
      'cmi.interactions._children': 'id,type,objectives,timestamp,correct_responses,weighting,learner_response,result,latency,description',
    };
    return childrenMap[element] || '';
  }

  private notifyDataChange() {
    if (this.onDataChange) {
      this.onDataChange({ ...this.data }, '2004');
    }
  }

  getData(): Record<string, string> {
    return { ...this.data };
  }
}

const ScormPlayer: React.FC<ScormPlayerProps> = ({
  contentUrl,
  lessonId,
  lessonSlug,
  courseSlug,
  onComplete,
  onProgress,
  onNavigateNext,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const api12Ref = useRef<Scorm12API | null>(null);
  const api2004Ref = useRef<Scorm2004API | null>(null);
  const completionReportedRef = useRef(false);
  const [notification, setNotification] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Listen for postMessage from SCORM content (e.g., navigation requests)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the message is from our iframe
      if (event.data?.type === 'lessonComplete') {
        console.log('[SCORM Player] Received lessonComplete message:', event.data);
        if (onNavigateNext) {
          onNavigateNext();
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [onNavigateNext]);

  const reportCompletion = useCallback(async (scormData: ScormData) => {
    if (completionReportedRef.current) return;
    completionReportedRef.current = true;

    try {
      const result = await courseApi.completeLesson(courseSlug, lessonSlug, {
        score: scormData.scoreRaw ? parseInt(scormData.scoreRaw) : undefined,
      });

      console.log('[SCORM Player] Completion reported to server:', result.data);

      let message = result.data.courseCompleted
        ? `Modul abgeschlossen! +${result.data.pointsAwarded} Punkte`
        : `Lektion abgeschlossen! +${result.data.pointsAwarded} Punkte`;

      if (result.data.badgesEarned && result.data.badgesEarned.length > 0) {
        message += ` 🏆 Badge erhalten!`;
      }

      setNotification({
        open: true,
        message,
        severity: 'success',
      });

      if (onComplete) {
        onComplete(scormData);
      }
    } catch (error: any) {
      console.error('[SCORM Player] Failed to report completion:', error);
      if (error.response?.data?.alreadyCompleted) {
        // Keep the flag true - lesson was already completed
        setNotification({
          open: true,
          message: 'Lektion wurde bereits abgeschlossen.',
          severity: 'info',
        });
      } else {
        // Only reset flag for real errors, allowing retry
        completionReportedRef.current = false;
        setNotification({
          open: true,
          message: 'Fehler beim Speichern des Fortschritts.',
          severity: 'error',
        });
      }
    }
  }, [courseSlug, lessonSlug, onComplete]);

  const handleDataChange = useCallback((data: Record<string, string>, version: string) => {
    let scormData: ScormData;

    if (version === '2004') {
      // SCORM 2004 data model
      scormData = {
        lessonId,
        completionStatus: data['cmi.completion_status'] || 'not attempted',
        successStatus: data['cmi.success_status'] || 'unknown',
        scoreRaw: data['cmi.score.raw'] || '',
        scoreMin: data['cmi.score.min'] || '0',
        scoreMax: data['cmi.score.max'] || '100',
        totalTime: data['cmi.total_time'] || '',
        suspendData: data['cmi.suspend_data'] || '',
        location: data['cmi.location'] || '',
      };

      // Check for completion in SCORM 2004
      const completionStatus = data['cmi.completion_status'];
      const successStatus = data['cmi.success_status'];
      if (completionStatus === 'completed' || successStatus === 'passed') {
        reportCompletion(scormData);
      }
    } else {
      // SCORM 1.2 data model
      scormData = {
        lessonId,
        completionStatus: data['cmi.core.lesson_status'] || 'not attempted',
        successStatus: data['cmi.success_status'] || '',
        scoreRaw: data['cmi.core.score.raw'] || '',
        scoreMin: data['cmi.core.score.min'] || '0',
        scoreMax: data['cmi.core.score.max'] || '100',
        totalTime: data['cmi.core.total_time'] || '',
        suspendData: data['cmi.suspend_data'] || '',
        location: data['cmi.core.lesson_location'] || '',
      };

      // Check for completion in SCORM 1.2
      const status = data['cmi.core.lesson_status'];
      if (status === 'completed' || status === 'passed') {
        reportCompletion(scormData);
      }
    }

    // Report progress
    if (onProgress) {
      onProgress(scormData);
    }
  }, [lessonId, onProgress, reportCompletion]);

  useEffect(() => {
    // Create both SCORM API instances
    api12Ref.current = new Scorm12API(handleDataChange);
    api2004Ref.current = new Scorm2004API(handleDataChange);

    // Expose APIs on window
    // SCORM 1.2 content looks for API
    // SCORM 2004 content looks for API_1484_11
    (window as any).API = api12Ref.current;
    (window as any).API_1484_11 = api2004Ref.current;

    console.log('[SCORM Player] APIs installed: window.API (1.2) and window.API_1484_11 (2004)');

    return () => {
      // Cleanup
      delete (window as any).API;
      delete (window as any).API_1484_11;
      console.log('[SCORM Player] APIs removed from window');
    };
  }, [handleDataChange]);

  return (
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
      <iframe
        ref={iframeRef}
        src={contentUrl}
        style={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          border: 'none',
        }}
        title="SCORM Content"
        allow="fullscreen"
      />
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ScormPlayer;
